// src/pages/msp-inventory/pages/reports/ValuationReportPage.jsx
import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import KpiCard from "../../components/reports/KpiCard";
import ClickableSkuCell from "../../components/reports/ClickableSkuCell";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getValuationReport, getValuationHistory, withPageRetry } from "../../services/catalogService";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import { useImageBackfill } from "../../hooks/useImageBackfill";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import { usePersistedState } from "../../hooks/usePersistedState";
import RefreshableSection from "../../components/reports/RefreshableSection";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { exportSkuReportCsv } from "../../utils/exportCsv";
import LoadingGame from "../../components/reports/LoadingGame";
import { History } from "lucide-react";
import MonthEndValuationReminder from "../../components/reports/MonthEndValuationReminder";

const COLORS = ["#0d9488", "#2563eb", "#9333ea", "#f97316", "#db2777", "#0891b2"];

// Local-date helpers for the "As of" filter (YYYY-MM-DD, no UTC shift).
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const endOfLastMonth = () => { const n = new Date(); return isoLocal(new Date(n.getFullYear(), n.getMonth(), 0)); };
const endOfLastYear = () => isoLocal(new Date(new Date().getFullYear() - 1, 11, 31));
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return isoLocal(d); };
const fmtAsOf = (s) => (s ? `${s.slice(5, 7)}/${s.slice(8, 10)}/${s.slice(2, 4)}` : "");
// Earliest date the estimate can go back to: the LATER of 6 months ago or this fixed date.
const HISTORY_FLOOR = "2026-07-01";
const minPastDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    const sixMonthsAgo = isoLocal(d);
    return sixMonthsAgo > HISTORY_FLOOR ? sixMonthsAgo : HISTORY_FLOOR;
};
// Month-ends before today, newest first, only back to the allowed floor (YYYY-MM-DD)
const monthEndOptions = () => {
    const n = new Date();
    const min = minPastDate();
    return Array.from({ length: 24 }, (_, i) => isoLocal(new Date(n.getFullYear(), n.getMonth() - i, 0)))
        .filter((d) => d >= min);
};
const monthEndLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const ValuationReportPage = ({ onOpenSku, inventorySyncStatus }) => {
    const [filters, setFilters] = usePersistedState("reports:valuation:filters", { groupBy: "fulfillment", basis: "cost" });
    const { groupBy, basis } = filters;
    const setGroupBy = (v) => setFilters((f) => ({ ...f, groupBy: v }));
    const setBasis = (v) => setFilters((f) => ({ ...f, basis: v }));
    // "" = Live (always the default on page load). A past date (YYYY-MM-DD) is only
    // used after the user opens "View a past date (estimate)" and picks one.
    const [asOf, setAsOf] = useState("");
    const [showPast, setShowPast] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [savedAt, setSavedAt] = useState(null);

    const cacheKey = buildCacheKey("valuation", asOf ? { groupBy, basis, asOf } : { groupBy, basis });

    // forceRefresh=true skips the cache read entirely (used by the manual
    // "Refresh Report" button, and by the auto-refresh timer once the
    // cache's 30-minute TTL is reached) — every other call checks
    // IndexedDB first, so toggling groupBy/basis back and forth within the
    // window is instant instead of re-hitting the backend.
    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                setLoadError("");
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        setLoadError("");
        try {
            const d = await withPageRetry(() => asOf
                ? getValuationHistory(asOf, groupBy, basis)
                : getValuationReport(groupBy, basis));
            setData(d);
            const now = Date.now();
            setSavedAt(now);
            setCachedReport(cacheKey, d);
        } catch (e) {
            // Deliberately keeps whatever `data` is already on screen rather
            // than clearing it — a failed refresh shouldn't blank out a
            // previously-good report, just surface that this refresh didn't
            // land.
            setLoadError(e.message || "Failed to load valuation report");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupBy, basis, asOf]);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    const patchRow = (id, patch) => {
        setData((prev) => prev ? {
            ...prev,
            rows: prev.rows.map((r) => r.catalog_item_id === id ? { ...r, ...patch } : r),
        } : prev);
    };

    const valueKey = basis === "sale" ? "total_sale_value" : "total_cost_value";
    const unitKey = basis === "sale" ? "unit_price" : "unit_cost";
    const sfKey = basis === "sale" ? "sf_sale_value" : "sf_cost_value";
    const wfsKey = basis === "sale" ? "wfs_sale_value" : "wfs_cost_value";
    const inboundKey = basis === "sale" ? "inbound_sale_value" : "inbound_cost_value";

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(data?.rows || [], { key: valueKey, dir: "desc" }, "reports:valuation:table");

    // Walks the FULL sorted table top-to-bottom, not just the visible page —
    // switching sort re-triggers this from row #1 of the new order, skipping
    // whatever's already been fetched this session.
    useImageBackfill(sorted, patchRow);

    if (loading && !data) return <LoadingGame label="Loading valuation..." />;
    if (loadError && !data) {
        return (
            <div className="max-w-lg mx-auto py-16 text-center">
                <p className="text-sm text-red-700 mb-3">{loadError}</p>
                <button onClick={loadReport} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition">
                    Retry
                </button>
            </div>
        );
    }
    if (!data) return null;

    const { totals, by_flag } = data;
    const pieData = groupBy === "flag"
        ? by_flag.map((g) => ({ name: g.flag, value: basis === "sale" ? g.total_sale_value : g.total_cost_value }))
        : [
            { name: "SF Stock", value: totals[sfKey] },
            { name: "WFS Stock", value: totals[wfsKey] },
            { name: "Inbound (WFS)", value: totals[inboundKey] },
        ];

    // Same grouping shape as pieData, but UNITS instead of dollar value —
    // by_flag rows and totals both already carry a raw `units`/`*_units`
    // count alongside the value fields, so no new data is needed.
    const unitPieData = groupBy === "flag"
        ? by_flag.map((g) => ({ name: g.flag, value: g.units }))
        : [
            { name: "SF Stock", value: totals.sf_units },
            { name: "WFS Stock", value: totals.wfs_units },
            { name: "Inbound (WFS)", value: totals.inbound_units },
        ];

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex flex-col gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Inventory Valuation</h1>
                    <p className="text-gray-500 text-sm">
                        {asOf
                            ? <>Estimated as of end of day {fmtAsOf(asOf)} (Eastern): stock quantity × {basis === "sale" ? "today's sale price" : "your cost prices"}</>
                            : <>Live snapshot — stock quantity × {basis === "sale" ? "your sale price" : "your cost prices"}</>}
                    </p>
                    {!asOf && inventorySyncStatus?.running && (
                        <p className="text-xs text-blue-600 mt-1 flex items-start gap-1.5">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mt-1 flex-shrink-0" />
                            Inventory sync in progress — stock numbers below may still be settling as it corrects them. Switching filters or reloading right now can show a different total than once the sync finishes.
                        </p>
                    )}
                    {loadError && data && (
                        <p className="text-xs text-orange-600 mt-1">
                            Last refresh failed ({loadError}) — showing the previous result. <button onClick={() => loadReport(true)} className="underline font-bold">Retry</button>
                        </p>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
                        <ExportCsvButton onExport={() => exportSkuReportCsv({
                            filename: `inventory_valuation_${basis}_${asOf ? `asof_${asOf}` : new Date().toISOString().slice(0, 10)}.csv`,
                            rows: sorted,
                            idKey: "catalog_item_id",
                            headers: ["Item", "SKU", basis === "sale" ? "Sale Price" : "Unit Cost", "SF Qty", "WFS Qty", "Inbound Qty", "Sales/mo", "Total Value"],
                            getRowValues: (r) => [r.title, r.sku, r[unitKey], r.sf_qty, r.wfs_qty, r.inbound_qty, r.sales_per_month, r[valueKey]],
                            summaryLines: [
                                ["Total Valuation", (totals[sfKey] + totals[wfsKey] + totals[inboundKey]).toFixed(2)],
                                ["SF Value", totals[sfKey].toFixed(2)],
                                ["WFS Value", totals[wfsKey].toFixed(2)],
                                ["Inbound Value", totals[inboundKey].toFixed(2)],
                                ["Total Units", totals.units],
                            ],
                        })} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {!showPast ? (
                            <button onClick={() => setShowPast(true)}
                                title="See an estimated valuation for any past date, e.g. end of last month"
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition whitespace-nowrap">
                                <History size={14} />
                                View a past date (estimate)
                            </button>
                        ) : (
                            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                                <button onClick={() => { setAsOf(""); setShowPast(false); }}
                                    className="px-3 py-1.5 text-xs font-bold rounded-md transition whitespace-nowrap text-gray-500 hover:text-teal-700">
                                    ← Back to Live
                                </button>
                                <button onClick={() => setAsOf(endOfLastMonth())} className={`px-3 py-1.5 text-xs font-bold rounded-md transition whitespace-nowrap ${asOf && asOf === endOfLastMonth() ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                                    End of last month
                                </button>
                                {endOfLastYear() >= minPastDate() && (
                                    <button onClick={() => setAsOf(endOfLastYear())} className={`px-3 py-1.5 text-xs font-bold rounded-md transition whitespace-nowrap ${asOf && asOf === endOfLastYear() ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                                        End of last year
                                    </button>
                                )}
                                <select value={monthEndOptions().includes(asOf) ? asOf : ""}
                                    onChange={(e) => { if (e.target.value) setAsOf(e.target.value); }}
                                    title="Pick any month-end"
                                    className={`px-2 py-1 text-xs rounded-md border bg-white focus:outline-none focus:border-teal-500 ${asOf && monthEndOptions().includes(asOf) ? "border-teal-400" : "border-gray-200"}`}>
                                    <option value="">Month-end…</option>
                                    {monthEndOptions().map((d) => <option key={d} value={d}>{monthEndLabel(d)}</option>)}
                                </select>
                                <input type="date" value={asOf} min={minPastDate()} max={yesterday()}
                                    onChange={(e) => { const v = e.target.value; if (!v || v >= minPastDate()) setAsOf(v); }}
                                    title="Or pick any past date"
                                    className={`px-2 py-1 text-xs rounded-md border focus:outline-none focus:border-teal-500 ${asOf ? "border-teal-400 bg-white" : "border-gray-200 bg-white"}`} />
                            </div>
                        )}
                        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                            <button onClick={() => setBasis("cost")} className={`px-3 py-1.5 text-xs font-bold rounded-md transition whitespace-nowrap ${basis === "cost" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                                Cost Value
                            </button>
                            <button onClick={() => setBasis("sale")} className={`px-3 py-1.5 text-xs font-bold rounded-md transition whitespace-nowrap ${basis === "sale" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                                Sale Value
                            </button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button onClick={() => setGroupBy("fulfillment")} className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 whitespace-nowrap ${groupBy === "fulfillment" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>By Fulfillment</button>
                            <button onClick={() => setGroupBy("flag")} className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 whitespace-nowrap ${groupBy === "flag" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>By Flag</button>
                        </div>
                    </div>
                </div>
            </div>

            {!asOf && (
                <MonthEndValuationReminder wrapperClassName="mb-4" hint="Use the Download button at the top of this page." />
            )}

            {asOf && data.meta && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900">
                    <p className="font-bold mb-1">Estimated valuation as of end of day {fmtAsOf(data.meta.as_of)} (Eastern)</p>
                    <p>
                        <b>{data.meta.items_from_snapshot}</b> items from saved daily stock snapshots (most accurate) ·{" "}
                        <b>{data.meta.items_estimated}</b> rolled back from today's stock using orders and replens ·{" "}
                        <b>{data.meta.items_not_listed_yet}</b> not in your catalog yet.
                    </p>
                    <p className="mt-1 text-blue-700">
                        Inbound isn't tracked historically (shown as 0). Sale value and fees use today's prices. Replen items use the lot costs on hand at that date.
                        {data.meta.items_cost_edited_after > 0 && <> <b>{data.meta.items_cost_edited_after}</b> items in stock then had their cost changed after this date, so today's cost is used for them.</>}
                        {" "}Restocks not logged as replens will make past stock look higher than it was.
                    </p>
                </div>
            )}

            <RefreshableSection refreshing={refreshing} className="mb-6">
                {(() => {
                    const avgLabel = (value, units) =>
                        (units || 0) > 0 ? `${units} units · avg $${(value / units).toFixed(2)}/unit` : undefined;

                    const totalValue = totals[sfKey] + totals[wfsKey] + totals[inboundKey];
                    const totalCostBasis = totals.sf_cost_value + totals.wfs_cost_value + totals.inbound_cost_value;

                    const totalCard = (
                        <KpiCard
                            label={`Total ${basis === "sale" ? "Sale" : "Cost"} Valuation${asOf ? ` (${fmtAsOf(asOf)})` : ""}`}
                            value={`$${totalValue.toFixed(2)}`}
                            sublabel={avgLabel(totalValue, totals.units)}
                            color="green"
                        />
                    );
                    const profitCard = (
                        <KpiCard label="Est. Profit if Sold Today" value={`$${totals.potential_profit.toFixed(2)}`}
                            sublabel={`$${totalCostBasis.toFixed(2)} costs and $${totals.potential_fees.toFixed(2)} fees`}
                            color={totals.potential_profit >= 0 ? "green" : "red"} />
                    );

                    if (groupBy === "flag") {
                        const valueKeyByFlag = basis === "sale" ? "total_sale_value" : "total_cost_value";
                        const sortedFlags = [...(by_flag || [])].sort((a, b) => b[valueKeyByFlag] - a[valueKeyByFlag]);
                        return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                                {totalCard}
                                {sortedFlags.map((g, i) => {
                                    const palette = ["blue", "teal", "purple", "orange", "pink"];
                                    return (
                                        <KpiCard key={g.flag} label={`${g.flag} Value`} value={`$${g[valueKeyByFlag].toFixed(2)}`}
                                            sublabel={avgLabel(g[valueKeyByFlag], g.units)}
                                            color={palette[i % palette.length]} />
                                    );
                                })}
                                {profitCard}
                            </div>
                        );
                    }

                    const sfUnits = totals.sf_units;
                    const wfsUnits = totals.wfs_units;
                    const inboundUnits = totals.inbound_units;

                    return (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {totalCard}
                            <KpiCard label="SF Value" value={`$${totals[sfKey].toFixed(2)}`}
                                sublabel={avgLabel(totals[sfKey], sfUnits)} color="blue" />
                            <KpiCard label="WFS Value" value={`$${totals[wfsKey].toFixed(2)}`}
                                sublabel={avgLabel(totals[wfsKey], wfsUnits)} color="teal" />
                            <KpiCard label="Inbound Value" value={`$${totals[inboundKey].toFixed(2)}`}
                                sublabel={avgLabel(totals[inboundKey], inboundUnits)} color="purple" />
                            {profitCard}
                        </div>
                    );
                })()}
            </RefreshableSection>

            <RefreshableSection refreshing={refreshing} className="mb-6">
                <div className="flex flex-col gap-6">
                    <div className="flex flex-row gap-6">
                        <div className="bg-white rounded-2xl shadow-lg p-5 w-full max-w">
                            <h3 className="font-bold text-gray-800 text-sm mb-3">Value Breakdown</h3>
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `$${e.value.toFixed(0)}`} isAnimationActive={false}>
                                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg p-5 w-full max-w ml-auto">
                            <h3 className="font-bold text-gray-800 text-sm mb-3">Stock Breakdown (Units)</h3>
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie data={unitPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.value.toLocaleString()}`} isAnimationActive={false}>
                                        {unitPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `${Number(v).toLocaleString()} units`} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg p-5 w-full">
                        <h3 className="font-bold text-gray-800 text-sm mb-3">By Item</h3>
                        <div className="overflow-x-auto -mx-5 px-5 max-h-[70vh] overflow-y-auto">
                            <table className="w-full text-sm min-w-[900px]">
                                <thead className="sticky top-0 z-10 bg-white">
                                    <tr className="text-left text-gray-400 border-b border-gray-100">
                                        <SortableTH label="Item" sortKey="title" sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label={basis === "sale" ? "Sale Price" : "Unit Cost"} sortKey={unitKey} sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="SF Qty" sortKey="sf_qty" sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="WFS Qty" sortKey="wfs_qty" sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="Inbound" sortKey="inbound_qty" sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="Sales/mo" sortKey="sales_per_month" sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="Total Value" sortKey={valueKey} sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="Est. Fees" sortKey="unit_fees" sortConfig={sortConfig} onSort={requestSort} />
                                        <SortableTH label="Profit/ROI" sortKey="estimated_profit_per_unit" sortConfig={sortConfig} onSort={requestSort} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageItems.map((r) => (
                                        <tr key={r.catalog_item_id} className="border-b border-gray-50">
                                            <td className="py-3 pr-4"><ClickableSkuCell item={r} onOpen={onOpenSku} /></td>
                                            <td className="py-3 pr-4 whitespace-nowrap">${r[unitKey].toFixed(2)}</td>
                                            <td className="py-3 pr-4">
                                                {r.sf_qty}
                                                {r.stock_source === "estimated" && <span className="ml-1 text-[9px] font-bold text-blue-500 align-top" title="Estimated from today's stock, orders and replens">est</span>}
                                            </td>
                                            <td className="py-3 pr-4">
                                                {r.wfs_qty}
                                                {r.stock_source === "estimated" && <span className="ml-1 text-[9px] font-bold text-blue-500 align-top" title="Estimated from today's stock, orders and replens">est</span>}
                                            </td>
                                            <td className="py-3 pr-4">{r.inbound_qty}</td>
                                            <td className="py-3 pr-4">{r.sales_per_month}</td>
                                            <td className="py-3 pr-4 font-bold whitespace-nowrap">${r[valueKey].toFixed(2)}</td>
                                            <td className="py-3 pr-4 relative group cursor-help whitespace-nowrap">
                                                <span className="text-red-500">${r.unit_fees.toFixed(2)}</span>
                                                {Object.keys(r.fee_breakdown || {}).length > 0 && (
                                                    <div className="hidden group-hover:block absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-52 text-left normal-case">
                                                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown / Unit</p>
                                                        <div className="space-y-1 text-xs">
                                                            {Object.entries(r.fee_breakdown).map(([k, v]) => v ? (
                                                                <div key={k} className="flex justify-between">
                                                                    <span className="text-gray-500">{k.replace(/_/g, " ")}</span>
                                                                    <span className="font-semibold text-gray-700">${Number(v).toFixed(2)}</span>
                                                                </div>
                                                            ) : null)}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 whitespace-nowrap">
                                                <span className={`font-bold ${r.estimated_profit_per_unit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                    ${r.estimated_profit_per_unit.toFixed(2)}
                                                </span>
                                                {r.estimated_roi != null && (
                                                    <span className="text-[10px] text-purple-600 font-semibold ml-1">({r.estimated_roi.toFixed(1)}%)</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {pageItems.length === 0 && (
                                        <tr><td colSpan={9} className="py-8 text-center text-gray-400">No items</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                    </div>
                </div>
            </RefreshableSection>
        </div>
    );
};

export default ValuationReportPage;